import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useReturnDetails } from 'pages/exchange-and-return/hooks';
import {
  CustomerAndPayment,
  Loading,
  ReturnHeader,
  ReturnHistoryAndTracking,
  ReturnSteps,
} from 'components';
import {
  RETURN_STATUSES,
  ReturnStepsEnum,
  outletRoutes,
  routes,
} from 'commons';
import * as S from './styles';

function getStep(status: string = ''): ReturnStepsEnum {
  const {
    CREATED,
    PRODUCT_RECEIVED,
    AWAITING_CUSTOMER_SHIPMENT,
    AWAITING_CUSTOMER_DROPOFF,
    AWAITING_PRODUCT_ARRIVAL,
    RETURN_CONFIRMED,
    REFUNDED,
    REFUND_FAILED,
    CANCELED,
  } = RETURN_STATUSES;

  switch (status) {
    case CREATED.value:
      return ReturnStepsEnum.Created;

    case AWAITING_CUSTOMER_SHIPMENT.value:
    case AWAITING_CUSTOMER_DROPOFF.value:
    case AWAITING_PRODUCT_ARRIVAL.value:
    case PRODUCT_RECEIVED.value:
      return ReturnStepsEnum.Post;

    case RETURN_CONFIRMED.value:
    case REFUND_FAILED.value:
      return ReturnStepsEnum.Received;

    case REFUNDED.value:
      return ReturnStepsEnum.Refund;

    case CANCELED.value:
      return ReturnStepsEnum.Cancelled;

    default:
      return ReturnStepsEnum.None;
  }
}

export function ReturnDetails() {
  const navigate = useNavigate();
  const {
    returnDetails,
    validatingReturnDetails,
    consignmentDetails,
    validatingConsignmentsDetails,
  } = useReturnDetails();

  const step = getStep(returnDetails?.status);
  const isPostOffice = returnDetails?.option?.type === 'POST_OFFICE';

  useEffect(() => {
    const param = `?modalidade=${
      isPostOffice ? 'correios' : 'entregue-na-loja'
    }`;

    switch (step) {
      case ReturnStepsEnum.Created:
        const { stepCreated } = outletRoutes;
        navigate(`${stepCreated.path}${param}`);
        break;

      case ReturnStepsEnum.Post:
        const { stepPost } = outletRoutes;
        navigate(`${stepPost.path}${param}`);
        break;

      case ReturnStepsEnum.Received:
        const { stepReceived } = outletRoutes;
        navigate(`${stepReceived.path}${param}`);
        break;

      case ReturnStepsEnum.Refund:
        const { stepCompleted } = outletRoutes;
        navigate(`${stepCompleted.path}${param}`);
        break;

      case ReturnStepsEnum.Cancelled:
        const { stepCancelled } = outletRoutes;
        navigate(`${stepCancelled.path}${param}`);
        break;
    }
  }, [isPostOffice, navigate, step]);

  if (
    !validatingReturnDetails &&
    !returnDetails &&
    !validatingConsignmentsDetails &&
    !consignmentDetails
  ) {
    navigate(routes.exchangeAndReturn.path);

    return null;
  }

  if (validatingReturnDetails && !returnDetails) {
    return <Loading />;
  }

  if (validatingConsignmentsDetails && !consignmentDetails) {
    return <Loading />;
  }

  if (!returnDetails || !consignmentDetails) {
    return <Loading />;
  }

  const { protocol } = returnDetails;

  return (
    <S.Main key={protocol}>
      <ReturnHeader
        consignment={consignmentDetails}
        returnDetails={returnDetails}
      />

      <S.Wrapper>
        <ReturnSteps currentStep={step} isPostOffice={isPostOffice} />

        <Outlet />

        <ReturnHistoryAndTracking protocol={protocol} />

        <CustomerAndPayment consignment={consignmentDetails} />
      </S.Wrapper>
    </S.Main>
  );
}
